<template>
  <SocialWallModalShell :visible="visible"
                        :aria-label="$t('general.liked_users')"
                        :close-label="$t('general.close_liked_users_window')"
                        size="compact"
                        v-on="$listeners">
    <template #title>
      <div class="interactive-social-wall__liked-modal-title">
        <i aria-hidden="true" class="uil-heart"/>
        <span>{{ $t('general.liked_users') }}</span>
        <small v-if="!isLoading">{{ likedUsers.length }}</small>
      </div>
    </template>

    <div class="interactive-social-wall__liked-modal-body">
      <div v-if="isLoading" class="interactive-social-wall__liked-state"
           role="status">
        <span aria-hidden="true" class="interactive-social-wall__state-spinner"/>
        <span>{{ $t('general.liked_users_loading') }}</span>
      </div>

      <div v-else-if="loadError"
           class="interactive-social-wall__liked-state interactive-social-wall__liked-state--error"
           role="alert">
        <i aria-hidden="true" class="uil-exclamation-circle"/>
        <span>{{ loadError }}</span>
        <button type="button" @click="$emit('retry')">{{ $t('general.try_again') }}</button>
      </div>

      <div v-else-if="!likedUsers.length" class="interactive-social-wall__liked-state">
        <i aria-hidden="true" class="uil-heart-break"/>
        <span>{{ $t('general.no_likes_yet') }}</span>
      </div>

      <div v-else class="interactive-social-wall__liked-list">
        <article v-for="likedUserItem in likedUserListItems"
                 :key="likedUserItem.key"
                 class="interactive-social-wall__liked-user">

          <SocialWallAvatar :name="likedUserItem.displayName" :photo="likedUserItem.photoUrl"
                            class="interactive-social-wall__liked-user-avatar"/>

          <div class="interactive-social-wall__liked-user-info">
            <strong>{{ likedUserItem.displayName }}</strong>
            <small v-if="likedUserItem.roleLabel">
              {{ likedUserItem.roleLabel }}
            </small>
          </div>
          <i aria-hidden="true" class="uil-heart"/>
        </article>
      </div>
    </div>
  </SocialWallModalShell>
</template>

<script>
import UserProfileAvatar from "../../../../components/common/UserProfileAvatar.vue";
import SocialWallModalShell from "./SocialWallModalShell.vue";
import {
  likedUserRoleLabel,
  userName,
  userPhoto,
} from "../utils/presentation";

export default {
  name: "SocialWallLikesModal",
  components: {
    SocialWallAvatar: UserProfileAvatar,
    SocialWallModalShell,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    likedUsers: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    loadError: {
      type: String,
      default: "",
    },
  },
  computed: {
    likedUserListItems() {
      return this.likedUsers.map((user, index) => {
        const key = `interactive-social-wall-liked-user-${user?.id ?? index}`;

        return {
          key,
          displayName: userName(user),
          photoUrl: userPhoto(user),
          roleLabel: likedUserRoleLabel(user),
        };
      });
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__liked-modal-body,
.interactive-social-wall__liked-modal-body * {
  box-sizing: border-box;
}

.interactive-social-wall__liked-modal-body button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
  outline: none;
  box-shadow: none;
}

.interactive-social-wall__liked-modal-title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  color: #303846;
}

.interactive-social-wall__liked-modal-title > i {
  color: var(--iconColor);
  font-size: 18px;
}

.interactive-social-wall__liked-modal-title > span {
  color: #303846;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.interactive-social-wall__liked-modal-title > small {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 23px;
  height: 20px;
  padding: 0 7px;
  color: #4cae39;
  font-size: 9px;
  font-weight: 700;
  background: #edf8ea;
  border-radius: 10px;
}

.interactive-social-wall__liked-modal-body {
  flex: 1;
  min-height: 250px;
  padding: 8px 0 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: #ccd3dd transparent;
  scrollbar-width: thin;
}

.interactive-social-wall__liked-list {
  display: flex;
  flex-direction: column;
  padding: 0 10px;
}

.interactive-social-wall__liked-user {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
  padding: 10px 9px;
  border-bottom: 1px solid #edf0f4;
  border-radius: 7px;
  transition: background-color 150ms ease;
}

.interactive-social-wall__liked-user:last-child {
  border-bottom-color: transparent;
}

.interactive-social-wall__liked-user:hover {
  background: #f7faf7;
}

.interactive-social-wall__liked-user-avatar {
  display: inline-flex;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  overflow: hidden;
  font-size: 11px;
  font-weight: 700;
  border-radius: 50%;
}

.interactive-social-wall__liked-user-info {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.interactive-social-wall__liked-user-info strong {
  overflow: hidden;
  color: #222a36;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.interactive-social-wall__liked-user-info small {
  overflow: hidden;
  color: #7a8495;
  font-size: 8.5px;
  line-height: 1.4;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.interactive-social-wall__liked-user > i {
  flex: 0 0 auto;
  color: var(--iconColor);
  font-size: 15px;
}

.interactive-social-wall__liked-state {
  display: flex;
  min-height: 230px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 24px;
  color: #7a8495;
  font-size: 10px;
  line-height: 1.5;
  text-align: center;
}

.interactive-social-wall__liked-state > i {
  font-size: 29px;
}

.interactive-social-wall__liked-state--error {
  --iconColor: #ca4b4b;
  color: #b54343;
}

.interactive-social-wall__liked-state button {
  min-height: 32px;
  margin-top: 3px !important;
  padding: 0 12px !important;
  color: #ffffff !important;
  font-size: 9px !important;
  font-weight: 600 !important;
  background: #50b83e !important;
  border-radius: 6px;
}

.interactive-social-wall__state-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 1.5px solid rgba(104, 117, 139, 0.32);
  border-top-color: #50b83e;
  border-radius: 50%;
  animation: interactive-social-wall-spin 700ms linear infinite;
}

@keyframes interactive-social-wall-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
